import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Bot, Users, MessageSquare, CreditCard, TrendingUp, MapPin,
  Home, Building, Scale, Calculator, Factory, Wrench, Star, Shield,
  RefreshCw, Clock, ChevronDown
} from 'lucide-react';
import AdminLayout from './AdminLayout';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AGENT_ICONS = {
  legal: Scale,
  fiscal: Calculator,
  industrial: Factory,
  residencial: Home,
  tecnico: Wrench,
  regional: MapPin,
  atencion_premium: Star,
  ventas: TrendingUp,
  etica: Shield
};

const AGENT_COLORS = {
  legal: 'bg-blue-500',
  fiscal: 'bg-green-500',
  industrial: 'bg-orange-500',
  residencial: 'bg-purple-500',
  tecnico: 'bg-gray-500',
  regional: 'bg-red-500',
  atencion_premium: 'bg-yellow-500',
  ventas: 'bg-emerald-500',
  etica: 'bg-indigo-500'
};

export default function AdminUpimIA() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('30d');
  const [analytics, setAnalytics] = useState(null);
  const [recentActivity, setRecentActivity] = useState([]);

  useEffect(() => {
    const token = localStorage.getItem('upim_token');
    if (!token) {
      navigate('/login');
      return;
    }
    loadAnalytics(token);
  }, [navigate, period]);

  const loadAnalytics = async (token) => {
    try {
      setLoading(true);
      const [analyticsRes, activityRes] = await Promise.all([
        axios.get(`${API_URL}/api/upim-ia/analytics/dashboard?period=${period}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_URL}/api/upim-ia/analytics/user-activity?limit=10`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setAnalytics(analyticsRes.data);
      setRecentActivity(activityRes.data.recent_activity || []);
    } catch (error) {
      console.error('Error loading analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (num) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num?.toString() || '0';
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('es-MX', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading && !analytics) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-gray-400" />
        </div>
      </AdminLayout>
    );
  }

  const kpis = analytics?.kpis || {};

  return (
    <AdminLayout>
      <div className="p-6" data-testid="admin-upim-ia-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Bot className="w-7 h-7 text-yellow-500" />
              UPIM IA Analytics
            </h1>
            <p className="text-gray-500 text-sm">Métricas de uso del sistema de IA</p>
          </div>
          <div className="flex items-center gap-3">
            <select
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-yellow-500"
            >
              <option value="7d">Últimos 7 días</option>
              <option value="30d">Últimos 30 días</option>
              <option value="90d">Últimos 90 días</option>
              <option value="365d">Último año</option>
            </select>
            <button
              onClick={() => loadAnalytics(localStorage.getItem('upim_token'))}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
            >
              <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
          <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <MessageSquare className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{formatNumber(kpis.total_conversations)}</p>
                <p className="text-xs text-gray-500">Conversaciones</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <Bot className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{formatNumber(kpis.total_messages)}</p>
                <p className="text-xs text-gray-500">Mensajes IA</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <Users className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{formatNumber(kpis.active_users)}</p>
                <p className="text-xs text-gray-500">Usuarios activos</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
                <CreditCard className="w-5 h-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{formatNumber(kpis.paid_subscribers)}</p>
                <p className="text-xs text-gray-500">Suscriptores pagados</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{formatCurrency(kpis.monthly_revenue_mxn)}</p>
                <p className="text-xs text-gray-500">Ingresos/mes</p>
              </div>
            </div>
          </div>
        </div>

        {/* Two Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Usage by Agent */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
            <h3 className="font-semibold text-gray-900 mb-4">Uso por Agente</h3>
            <div className="space-y-3">
              {analytics?.usage_by_agent?.length > 0 ? (
                analytics.usage_by_agent.map((item, idx) => {
                  const Icon = AGENT_ICONS[item.agent] || Bot;
                  const colorClass = AGENT_COLORS[item.agent] || 'bg-gray-500';
                  const maxCount = Math.max(...analytics.usage_by_agent.map(a => a.count));
                  const percentage = (item.count / maxCount) * 100;
                  
                  return (
                    <div key={idx} className="flex items-center gap-3">
                      <div className={`w-8 h-8 ${colorClass} rounded-lg flex items-center justify-center`}>
                        <Icon className="w-4 h-4 text-white" />
                      </div>
                      <div className="flex-1">
                        <div className="flex justify-between text-sm mb-1">
                          <span className="font-medium text-gray-700">{item.agent_name}</span>
                          <span className="text-gray-500">{item.count}</span>
                        </div>
                        <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                          <div 
                            className={`h-full ${colorClass}`}
                            style={{ width: `${percentage}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  );
                })
              ) : (
                <p className="text-gray-500 text-sm text-center py-4">Sin datos de uso</p>
              )}
            </div>
          </div>

          {/* Top States */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
            <h3 className="font-semibold text-gray-900 mb-4">Top Estados</h3>
            <div className="space-y-3">
              {analytics?.top_states?.length > 0 ? (
                analytics.top_states.map((item, idx) => {
                  const maxCount = Math.max(...analytics.top_states.map(s => s.count));
                  const percentage = (item.count / maxCount) * 100;
                  
                  return (
                    <div key={idx} className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center">
                        <MapPin className="w-4 h-4 text-red-600" />
                      </div>
                      <div className="flex-1">
                        <div className="flex justify-between text-sm mb-1">
                          <span className="font-medium text-gray-700 capitalize">{item.state?.replace(/_/g, ' ')}</span>
                          <span className="text-gray-500">{item.count} consultas</span>
                        </div>
                        <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-red-500"
                            style={{ width: `${percentage}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  );
                })
              ) : (
                <p className="text-gray-500 text-sm text-center py-4">Sin datos de estados</p>
              )}
            </div>
          </div>
        </div>

        {/* Recent Activity */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
          <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Clock className="w-5 h-5 text-gray-400" />
            Actividad Reciente
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="text-left text-xs text-gray-500 border-b">
                  <th className="pb-3 font-medium">Usuario</th>
                  <th className="pb-3 font-medium">Agente</th>
                  <th className="pb-3 font-medium">Consulta</th>
                  <th className="pb-3 font-medium">Estado</th>
                  <th className="pb-3 font-medium">Mensajes</th>
                  <th className="pb-3 font-medium">Última actividad</th>
                </tr>
              </thead>
              <tbody>
                {recentActivity.length > 0 ? (
                  recentActivity.map((item, idx) => {
                    const Icon = AGENT_ICONS[item.agent_type] || Bot;
                    const colorClass = AGENT_COLORS[item.agent_type] || 'bg-gray-500';
                    
                    return (
                      <tr key={idx} className="border-b border-gray-50 hover:bg-gray-50">
                        <td className="py-3">
                          <div>
                            <p className="text-sm font-medium text-gray-900">{item.user_name || 'Usuario'}</p>
                            <p className="text-xs text-gray-500">{item.user_email}</p>
                          </div>
                        </td>
                        <td className="py-3">
                          <div className="flex items-center gap-2">
                            <div className={`w-6 h-6 ${colorClass} rounded flex items-center justify-center`}>
                              <Icon className="w-3 h-3 text-white" />
                            </div>
                            <span className="text-sm text-gray-700">{item.agent_name}</span>
                          </div>
                        </td>
                        <td className="py-3">
                          <p className="text-sm text-gray-700 truncate max-w-xs">{item.title || '-'}</p>
                        </td>
                        <td className="py-3">
                          <span className="text-sm text-gray-600 capitalize">{item.mexican_state?.replace(/_/g, ' ') || '-'}</span>
                        </td>
                        <td className="py-3">
                          <span className="text-sm font-medium text-gray-700">{item.message_count || 0}</span>
                        </td>
                        <td className="py-3">
                          <span className="text-sm text-gray-500">{formatDate(item.last_message_at)}</span>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={6} className="py-8 text-center text-gray-500">
                      No hay actividad reciente
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
